import Ajv from 'ajv';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorIcon from '@mui/icons-material/Error';
import CircularProgress from '@mui/material/CircularProgress';
import NewDefinitionDialog from './NewDefinitionDialog';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { BrainApi } from '@src/services/brain/brain-api';
import { colors } from '@src/styles';
import { useCanonicalsContext } from '../CanonicalsContext';
import DefinitionsListSection from './DefinitionsListSection';
import JsonViewerSection from './JsonViewerSection';
import CanonicalToggleSwitch from './CanonicalToggleSwitch';

export interface CanonicalOrganizationDetailsProps {
  organizationId: string;
  organizationName: string;
  onBack: () => void;
}

const CanonicalOrganizationDetails: React.FC<CanonicalOrganizationDetailsProps> = ({
  organizationId,
  organizationName,
  onBack,
}) => {
  const {
    selectedCanonical,
    organizationDefinitions,
    organizationDefinitionsError,
    organizationAuthStatus,
    isLoadingOrganizationAuthStatus,
    resourceDetails,
    publishedResourceDetails,
    isLoadingOrganizationDefinitions,
    hasMoreDefinitions,
    handleLoadMoreDefinitions,
    extensionSubscriptionsOrgIds,
    handleTexSubscriptions,
    handlePublishDefinition,
    refetchOrganizationDefinitions,
  } = useCanonicalsContext();
  const [viewMode, setViewMode] = useState<'schema' | 'raw'>('schema');
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string | null>(null);
  const selectedDefinition = organizationDefinitions?.find((def) => def.id === selectedDefinitionId);
  const [jsonEditorValue, setJsonEditorValue] = useState<string>('{}');
  const [jsonErrors, setJsonErrors] = useState<string[]>([]);
  const [isApplicationEnabled, setIsApplicationEnabled] = useState<boolean>(false);
  const [isTogglingSubscription, setIsTogglingSubscription] = useState<boolean>(false);
  const [canToggle, setCanToggle] = useState<boolean>(true);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [canPublish, setCanPublish] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const validateConfig = useMemo(() => {
    if (!resourceDetails?.schema) return null;
    try {
      return new Ajv({ allErrors: true, verbose: true }).compile(resourceDetails.schema);
    } catch {
      return null;
    }
  }, [resourceDetails?.schema]);
  const [newDefinitionVersion, setNewDefinitionVersion] = useState<string>('1.0.0');
  const [modalViewMode, setModalViewMode] = useState<'schema' | 'raw'>('schema');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    setSelectedDefinitionId(publishedResourceDetails?.definitions.find((d) => d.organizationId === organizationId)?.id);
  }, [publishedResourceDetails]);

  useEffect(() => {
    setIsApplicationEnabled(Boolean(extensionSubscriptionsOrgIds?.some((o) => o === organizationId)));
  }, [extensionSubscriptionsOrgIds]);

  const handleToggleSubscription = async (newValue: boolean) => {
    if (!canToggle || isTogglingSubscription) return;

    setIsTogglingSubscription(true);
    setCanToggle(false);

    try {
      await handleTexSubscriptions(organizationId, newValue ? 'add' : 'remove');
      setIsApplicationEnabled(newValue);
      setSnackbar({
        open: true,
        message: newValue ? 'Canônica ativada com sucesso!' : 'Canônica desativada com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Failed to toggle subscription:', error);
      setSnackbar({
        open: true,
        message: `Erro ao alterar status da canônica. ${error?.message ?? ''}`,
        severity: 'error',
      });
    } finally {
      setIsTogglingSubscription(false);
      // Prevent multiple clicks for 2 seconds
      setTimeout(() => {
        setCanToggle(true);
      }, 2000);
    }
  };

  const handlePublish = async () => {
    if (!canPublish || isPublishing || !selectedDefinition) return;

    setIsPublishing(true);
    setCanPublish(false);

    try {
      await handlePublishDefinition(selectedDefinition.id);
      setSnackbar({
        open: true,
        message: 'Definição publicada com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Failed to publish definition:', error);
      setSnackbar({
        open: true,
        message: `Erro ao publicar definição. ${error?.message ?? ''}`,
        severity: 'error',
      });
    } finally {
      setIsPublishing(false);
      // Prevent multiple clicks for 2 seconds
      setTimeout(() => {
        setCanPublish(true);
      }, 2000);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenModal = () => {
    // Calculate next version based on latest definition
    if (organizationDefinitions && organizationDefinitions.length > 0) {
      const latestVersion = organizationDefinitions[0].version; // Assuming sorted by version desc
      const versionParts = latestVersion.split('.').map(Number);
      versionParts[2] = (versionParts[2] || 0) + 1; // Increment patch version
      setNewDefinitionVersion(versionParts.join('.'));
    } else {
      setNewDefinitionVersion('1.0.0');
    }

    // Use current published definition as default config
    const currentPublishedDefinitionId = resourceDetails?.definitions?.find(
      (d) => d.organizationId === organizationId,
    )?.id;
    const currentPublishedDefinition = organizationDefinitions?.find((def) => def.id === currentPublishedDefinitionId);

    if (currentPublishedDefinition) {
      setJsonEditorValue(JSON.stringify(currentPublishedDefinition.config, null, 2));
    } else {
      setJsonEditorValue('{}');
    }

    setIsModalOpen(true);
    setModalViewMode('schema');
  };

  const handleCloseModal = () => {
    const currentPublishedDefinitionId = resourceDetails?.definitions?.find(
      (d) => d.organizationId === organizationId,
    )?.id;
    const currentPublishedDefinition = organizationDefinitions?.find((def) => def.id === currentPublishedDefinitionId);

    setIsModalOpen(false);
    setJsonEditorValue(JSON.stringify(currentPublishedDefinition.config, null, 2));
    setModalViewMode('schema');
  };

  const handleCreateDefinition = async () => {
    setIsCreating(true);
    try {
      const parsed = JSON.parse(jsonEditorValue);

      if (!newDefinitionVersion) throw new Error('Sem versão definida');
      if (!organizationId) throw new Error('Sem organização selecionada');

      const brainApiService = new BrainApi();
      await brainApiService.v1.resources.postDefinitionByResourceId({
        resourceId: resourceDetails.id,
        body: {
          config: parsed,
          organizationId: organizationId,
          version: newDefinitionVersion,
        },
      });

      handleCloseModal();
      setSnackbar({
        open: true,
        message: `Nova definição v${newDefinitionVersion} criada com sucesso!`,
        severity: 'success',
      });

      // Reload organization definitions to update the definitions list
      await refetchOrganizationDefinitions();
    } catch (e: unknown) {
      if (e instanceof Error) {
        setSnackbar({
          open: true,
          message: `Erro: ${e.message}`,
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Erro: JSON inválido',
          severity: 'error',
        });
      }
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    if (selectedDefinition) {
      setJsonEditorValue(JSON.stringify(selectedDefinition.config, null, 2));
      setJsonErrors([]);
    }
  }, [selectedDefinition]);

  const handleJsonChange = (value: string) => {
    setJsonEditorValue(value);

    try {
      const parsed = JSON.parse(value);

      // Validate against schema
      if (!validateConfig) {
        setJsonErrors([]);
        return;
      }
      const validate = validateConfig;
      const valid = validate(parsed);

      if (!valid && validate.errors) {
        const errors = validate.errors.map((err) => {
          return JSON.stringify({ ...err, parentSchema: undefined, data: undefined });
        });
        setJsonErrors(errors);
      } else {
        setJsonErrors([]);
      }
    } catch (err) {
      if (err instanceof Error) {
        setJsonErrors([`JSON Syntax Error: ${err.message}`]);
      }
    }
  };

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'schema' | 'raw' | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleModalViewModeChange = (newMode: 'schema' | 'raw') => {
    setModalViewMode(newMode);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        animation: 'slideInFromRight 0.4s ease-out',
        '@keyframes slideInFromRight': {
          '0%': {
            opacity: 0,
            transform: 'translateX(30px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          animation: 'fadeIn 0.5s ease-out 0.2s both',
          '@keyframes fadeIn': {
            '0%': {
              opacity: 0,
            },
            '100%': {
              opacity: 1,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Voltar para Canonical" arrow>
            <IconButton
              onClick={onBack}
              sx={{
                color: colors.primary.white,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: colors.background.secondary,
                  color: colors.primary.yellow,
                  transform: 'scale(1.1)',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" sx={{ color: colors.primary.white, fontWeight: 600 }}>
            {organizationName} - {selectedCanonical.key}
          </Typography>
          {isLoadingOrganizationAuthStatus ? (
            <CircularProgress size={20} sx={{ color: colors.primary.white }} />
          ) : (
            organizationAuthStatus !== 'OK' && (
              <Tooltip title="Favor chamar algum desenvolvedor para a criação do bot" arrow>
                <Box
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    animation: 'pulse 2s ease-in-out infinite',
                    transition: 'all 0.3s ease',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        opacity: 1,
                      },
                      '50%': {
                        opacity: 0.7,
                      },
                    },
                    '&:hover': {
                      transform: 'scale(1.01)',
                      animation: 'none',
                    },
                  }}
                >
                  <ErrorIcon
                    className="error-icon"
                    sx={{
                      color: colors.primary.warning,
                      fontSize: 24,
                      transition: 'all 0.3s ease',
                    }}
                  />
                  <Typography
                    className="error-text"
                    variant="body2"
                    sx={{
                      color: colors.primary.warning,
                      fontWeight: 500,
                      transition: 'color 0.3s ease',
                    }}
                  >
                    Organização sem bot configurado
                  </Typography>
                </Box>
              </Tooltip>
            )
          )}
        </Box>
        <CanonicalToggleSwitch
          isEnabled={isApplicationEnabled}
          isToggling={isTogglingSubscription}
          canToggle={canToggle}
          onToggle={handleToggleSubscription}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          gap: 2,
          height: '90%',
        }}
      >
        <DefinitionsListSection
          organizationDefinitions={organizationDefinitions}
          organizationDefinitionsError={organizationDefinitionsError}
          isLoadingOrganizationDefinitions={isLoadingOrganizationDefinitions}
          hasMoreDefinitions={hasMoreDefinitions}
          selectedDefinitionId={selectedDefinitionId}
          currentPublishedDefinitionId={
            publishedResourceDetails?.definitions?.find((d) => d.organizationId === organizationId)?.id
          }
          onSelectDefinition={setSelectedDefinitionId}
          onLoadMore={handleLoadMoreDefinitions}
          onOpenNewDefinitionModal={handleOpenModal}
        />

        <JsonViewerSection
          organizationId={organizationId}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          selectedDefinition={selectedDefinition}
          schema={resourceDetails?.schema}
          jsonEditorValue={jsonEditorValue}
          jsonErrors={jsonErrors}
          isPublishing={isPublishing}
          canPublish={canPublish}
          isPublishDisabled={
            !selectedDefinition ||
            (publishedResourceDetails?.definitions?.find((d) => d.organizationId === organizationId)?.id ===
              selectedDefinition.id &&
              Boolean(selectedDefinition.publishedAt))
          }
          onPublish={handlePublish}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            backgroundColor:
              snackbar.severity === 'success' ? colors.primary.green : colors.primary.warning || '#f44336',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <NewDefinitionDialog
        organizationId={organizationId}
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleCreateDefinition}
        version={newDefinitionVersion}
        onVersionChange={setNewDefinitionVersion}
        viewMode={modalViewMode}
        onViewModeChange={handleModalViewModeChange}
        jsonValue={jsonEditorValue}
        onJsonChange={handleJsonChange}
        jsonErrors={jsonErrors}
        schema={resourceDetails?.schema}
        isLoading={isCreating}
      />
    </Box>
  );
};

export default CanonicalOrganizationDetails;
